import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { revisionEtag } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type { Deployment, ParameterDefinition, ParameterValue } from '../api/types';
import { describeError, useToast } from '../components/toast';
import { strings } from '../strings';
import {
  conflictMessage,
  initialText,
  isUnchangedEdit,
  parameterInput,
  parseParameterText,
  saveParameter,
} from './parameterEdit';

const p = strings.variables.parameter;

/**
 * The inline editor of a parameter's value in the deployment (spec 6.10.11): typed by the declaration, saved as
 * a minimal merge patch with If-Match and the 412 rules of `saveParameter`.
 */
export function ParameterEditor({
  deployment,
  name,
  definition,
  onDone,
  onCancel,
}: {
  deployment: Deployment;
  name: string;
  definition: ParameterDefinition | undefined;
  onDone: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  // The value and ETag the user started from; a conflict replaces them with the server's.
  const [start] = useState(() => ({
    value: deployment.settings.parameters?.[name] as ParameterValue | undefined,
    etag: revisionEtag(deployment.revision),
  }));
  const [base, setBase] = useState(start);
  const [text, setText] = useState(() => initialText(start.value, definition));
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const control = useRef<HTMLInputElement & HTMLSelectElement>(null);
  const input = parameterInput(definition);

  useEffect(() => {
    control.current?.focus();
    if (control.current?.tagName === 'INPUT') control.current.select();
  }, []);

  const save = async () => {
    const parsed = parseParameterText(definition, text);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    if (!conflict && isUnchangedEdit(text, base.value, definition)) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const outcome = await saveParameter(api, {
        deploymentId: deployment.id,
        etag: base.etag,
        name,
        base: base.value,
        value: parsed.value,
      });
      queryClient.setQueryData(queryKeys.deployment(deployment.id), outcome.deployment);
      if (outcome.kind === 'conflict') {
        setConflict(conflictMessage(name, outcome.current));
        setBase({ value: outcome.current, etag: outcome.deployment.etag });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['deployment', deployment.id] });
      toast.show({
        tone: 'success',
        title: parsed.value === null ? p.savedDefault(name) : p.saved(name, String(parsed.value)),
      });
      onDone();
    } catch (failure) {
      const { title, detail } = describeError(failure);
      setError(detail ?? title);
    } finally {
      setBusy(false);
    }
  };

  const describedBy = [error && `${id}-error`, conflict && `${id}-conflict`, `${id}-note`]
    .filter(Boolean)
    .join(' ');
  const common = {
    id,
    'aria-label': p.label(name),
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    disabled: busy,
  } as const;
  return (
    <div
      className="var-param-editor"
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          void save();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      <div className="flex items-center gap-1">
        {input.kind === 'select' ? (
          <select
            ref={control}
            className="input min-w-0 flex-1"
            value={text}
            onChange={(event) => setText(event.target.value)}
            {...common}
          >
            {!input.options.some((o) => o.value === text) && <option value={text}>{text}</option>}
            {input.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={control}
            className="input min-w-0 flex-1 font-mono"
            type={input.kind === 'number' ? 'number' : 'text'}
            min={input.kind === 'number' ? input.minimum : undefined}
            max={input.kind === 'number' ? input.maximum : undefined}
            value={text}
            placeholder={definition?.default !== undefined ? String(definition.default) : undefined}
            onChange={(event) => setText(event.target.value)}
            {...common}
          />
        )}
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save()}>
          {busy ? p.saving : p.save}
        </button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={onCancel}>
          {p.cancel}
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="field-error">
          {error}
        </p>
      )}
      {conflict && (
        <p id={`${id}-conflict`} role="alert" className="var-param-conflict" data-testid="parameter-conflict">
          {conflict} {p.overwrite}
        </p>
      )}
      <p id={`${id}-note`} className="help">
        {definition ? p.note(name) : p.notDeclared(name)}
      </p>
    </div>
  );
}
